import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faBox,
  faRuler,
  faRupeeSign,
  faTags,
  faBarcode,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * Small presentational row for displaying product size information.
 */
const InfoRow = ({ icon, label, value, highlight = false }) => {
  const { t } = useTranslation();
  const emDash = t('common.symbols.emDash');
  const displayValue = value || emDash;
  return (
    <div
      className="flex items-start justify-between rounded-xl border px-4 py-3"
      style={{
        borderColor: UI_COLORS.BORDERS.LIGHT,
        backgroundColor: highlight
          ? colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)
          : UI_COLORS.BACKGROUNDS.CARD,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.3),
            color: UI_COLORS.TEXT.PRIMARY,
          }}
        >
          <FontAwesomeIcon icon={icon} className="text-base" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {label}
          </p>
          <p className="text-base font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {displayValue}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Modal that displays read-only product size information.
 */
const ProductSizeDetails = ({ isOpen, onClose, productSize, loading = false }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const strings = t('productSizes.view', { returnObjects: true });
  const emDash = t('common.symbols.emDash');
  const UNITS = t('common.units', { returnObjects: true });

  /**
   * Format price with rupee symbol and proper formatting
   */
  const formatPrice = (price) => {
    const currency = t('common.symbols.currency');
    if (!price && price !== 0) return emDash;
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return emDash;
    return `${currency}${numPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const aliasName = productSize?.alias_name || strings.header.fallbackTitle;
  const productSizeValue = productSize?.product_size || emDash;
  const productName = productSize?.product_name || emDash;
  // Format thickness, length and weight with units when values are present.
  const thickness = (productSize?.product_thickness !== null && productSize?.product_thickness !== undefined && productSize?.product_thickness !== '')
    ? `${productSize.product_thickness} ${UNITS.mm}`
    : emDash;
  const priceValue = formatPrice(productSize?.base_price);
  const categoryName = productSize?.category_name || productSize?.product_category?.name || emDash;
  const hsnCodeValue = productSize?.hsn_code_value || productSize?.hsn_code?.code || emDash;
  const hsnDescription = productSize?.hsn_description || productSize?.hsn_code?.description || emDash;
  const gstRate = (productSize?.gst_rate !== null && productSize?.gst_rate !== undefined && productSize?.gst_rate !== '')
    ? `${productSize.gst_rate}%`
    : emDash;
  const priceWithGst = formatPrice(productSize?.price_with_gst);
  const productWeight = (productSize?.product_weight !== null && productSize?.product_weight !== undefined && productSize?.product_weight !== '')
    ? `${productSize.product_weight} ${UNITS.kg}`
    : emDash;
  const standardLength = (productSize?.standard_length !== null && productSize?.standard_length !== undefined && productSize?.standard_length !== '')
    ? `${productSize.standard_length} ${UNITS.m}`
    : emDash;


  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.product-size-view-modal{ -ms-overflow-style: none; scrollbar-width: none; } .product-size-view-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="product-size-view-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {strings.header.sectionTitle}
            </p>
            <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {aliasName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
              ></div>
              <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.loading}
              </p>
            </div>
          ) : !productSize ? (
            <div className="rounded-xl border px-6 py-16 text-center" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <FontAwesomeIcon
                icon={faBox}
                className="text-4xl opacity-60"
                style={{ color: UI_COLORS.TEXT.MUTED }}
              />
              <h3 className="mt-4 text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.empty.title}
              </h3>
              <p className="mt-2 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {strings.empty.message}
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faBox} label={strings.fields.aliasName} value={aliasName} highlight />
                <InfoRow icon={faRuler} label={strings.fields.productSize} value={productSizeValue} highlight />
                <InfoRow icon={faTags} label={strings.fields.category} value={categoryName} />
                <InfoRow icon={faBox} label={strings.fields.productName} value={productName} />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <InfoRow icon={faRuler} label={strings.fields.productWeight} value={productWeight} />
                <InfoRow icon={faRuler} label={strings.fields.standardLength} value={standardLength} />
                <InfoRow icon={faRuler} label={strings.fields.thickness} value={thickness} />
              </div>
              {/* Base price, Price with GST and GST Rate - grouped together after thickness */}
              <div className="grid gap-4 md:grid-cols-3">
                <InfoRow icon={faRupeeSign} label={strings.fields.basePrice} value={priceValue} />
                <InfoRow icon={faRupeeSign} label={strings.fields.priceWithGst || 'Price (with GST)'} value={priceWithGst} />
                <InfoRow icon={faTags} label={strings.fields.gstRate || 'GST Rate'} value={gstRate} />
              </div>

              <div className="grid gap-4 md:grid-cols-1">
                <InfoRow icon={faBarcode} label={strings.fields.hsnCode} value={hsnCodeValue} />
                {/* HSN Description should appear above stock status */}
                <InfoRow icon={faBarcode} label={strings.fields.hsnDescription || 'HSN Description'} value={hsnDescription} />
              </div>
            </>
          )}
        </div>

        
      </div>
    </div>
  );
};

export default ProductSizeDetails;